import React, { useReducer } from "react";

import useIsMobile from "Utilities/Hooks/useIsMobile";
import { getSafeFundColor } from "Utilities/getSafeFundColor";

import calculateAddOnGiftAmount from "./PaymentFormScreen/AddOnGiftsSection/calculateAddOnGiftAmount";

const ctfData = window?.data?.paymentPageData?.ctfData || {};

const PaymentPageContext = React.createContext();

const defaultState = {
  tabID: null,
  user: {},
  userInitial: {}, // the initial user info when page loads (from window.data)
  network: {},
  fund: { paymentPageSettings: {} },
  modifyPayment: {},
  noteInfo: {},
  amount: null,
  authCodeData: {},
  paymentMethods: [],
  paymentMethodProviders: [],
  selectedPaymentMethodID: null,
  submitPaymentResponse: {},
  pollingPaymentResponse: {},
  recurringPaymentsOnlySuccess: null,
  currentScreen: null,
  screenControlFlags: {
    isManagingPaymentMethods: false,
    isModifyingScheduledPayment: false,
  },
  forcePageReload: null,
  isMobile: window.innerWidth <= 480,
  headerIsBig: undefined,
  accountIsPending: null, // is true when someone gives to a closed network and isn't already a member
  recurringPayment: {
    recurring: false,
    frequency: null, // "weekly", "monthly", "quarterly", "annually"
    startDate: null,
    endDate: null,
    editing: false,
  },
  applyToExistingPledge: true,
  memo: null,
  paymentParent: null,
  addOnAmounts: [],
  merchantToken: "",
};

const reducer = (state, action) => {
  switch (action.type) {
    case "handleWindowData": {
      return {
        ...state,
        network: {
          ...state.network,
          ...(action.payload.network || []),
        },
        fund: {
          ...action.payload.fund,
          fundColorSafe: getSafeFundColor(action?.payload?.fund?.fundColor),
        },
        user: action.payload.user,
        userInitial: action.payload.user,
        modifyPayment: action.payload.modifyPayment,
        noteInfo: action.payload.noteInfo,
        paymentMethods: action.payload.paymentMethods || [],
        selectedPaymentMethodID: updateSelectedPaymentMethodID({
          paymentMethods: action.payload.paymentMethods,
          modifyPayment: action.payload.modifyPayment,
        }),
        paymentMethodProviders: action.payload.paymentMethodProviders || [],
        merchantToken: action.payload.merchantToken,
      };
    }

    case "setUser": {
      return {
        ...state,
        user: action.payload,
      };
    }

    case "setSelectedPaymentMethodIDAndPaymentMethods": {
      const newState = {
        ...state,
        selectedPaymentMethodID: action.payload.selectedPaymentMethodID,
        paymentMethods: action.payload.paymentMethods,
      };
      return newState;
    }

    case "setPaymentMethods": {
      return {
        ...state,
        paymentMethods: action.payload,
        selectedPaymentMethodID: updateSelectedPaymentMethodID({
          paymentMethods: action.payload,
          currentlySelectedPaymentMethodID: state.selectedPaymentMethodID,
        }),
      };
    }

    case "setCurrentScreen": {
      return {
        ...state,
        currentScreen: action.screen,
      };
    }

    case "setScreenControlFlag": {
      return {
        ...state,
        screenControlFlags: {
          ...state.screenControlFlags,
          [action.flag]: action.value,
        },
      };
    }

    case "setAuthCodeData": {
      return {
        ...state,
        authCodeData: action.payload,
      };
    }

    case "setAuthEmailOrPhone": {
      return {
        ...state,
        authCodeData: {
          ...state.authCodeData,
          loginValueType: action.loginValueType,
          loginValue: action.loginValue,
        },
      };
    }

    case "addPaymentMethod": {
      return {
        ...state,
        paymentMethods: [...state.paymentMethods, action.payload],
      };
    }

    case "setSelectedPaymentMethodID": {
      return {
        ...state,
        selectedPaymentMethodID: action.payload,
      };
    }

    case "setSubmitPaymentResponse": {
      return {
        ...state,
        submitPaymentResponse: action.payload,
      };
    }

    case "setPollingPaymentResponse": {
      return {
        ...state,
        pollingPaymentResponse: action.payload,
      };
    }

    case "resetPaymentSubmit": {
      return {
        ...state,
        submitPaymentResponse: {},
        pollingPaymentResponse: {},
      };
    }

    case "setAmount": {
      return {
        ...state,
        amount: action.payload,
      };
    }

    case "setModifyPaymentAlreadyCompleted": {
      return {
        ...state,
        modifyPayment: {
          ...state.modifyPayment,
          paymentAlreadyCompleted: true,
        },
      };
    }

    case "setTabID": {
      return {
        ...state,
        tabID: action.payload,
      };
    }

    case "setHeaderIsBig": {
      return {
        ...state,
        headerIsBig: action.payload,
      };
    }

    case "forcePageReload": {
      return {
        ...state,
        forcePageReload: action.reason,
      };
    }

    case "logoutUser": {
      return {
        ...state,
        user: {},
        modifyPayment: {},
        authCodeData: {},
        paymentMethods: [],
        selectedPaymentMethodID: null,
        submitPaymentResponse: {},
        pollingPaymentResponse: {},
      };
    }

    case "setAccountIsPending": {
      return {
        ...state,
        accountIsPending: action.payload,
      };
    }

    case "setSuggestedRecurringData": {
      return {
        ...state,
        recurringPayment: {
          ...state.recurringPayment,
          startDate: action.startDate,
          endDate: action.endDate,
          frequency: action.frequency,
        },
      };
    }

    case "toggleRecurringPayment": {
      let recurring = !state.recurringPayment.recurring;
      let editing = !state.recurringPayment.editing;
      return {
        ...state,
        recurringPayment: {
          ...state.recurringPayment,
          recurring,
          editing,
        },
      };
    }

    case "selectRecurrenceFrequency": {
      return {
        ...state,
        recurringPayment: {
          ...state.recurringPayment,
          frequency: action.payload.id,
        },
      };
    }
    case "setStartDate": {
      return {
        ...state,
        recurringPayment: {
          ...state.recurringPayment,
          startDate: action.payload,
        },
      };
    }
    case "setEndDate": {
      return {
        ...state,
        recurringPayment: {
          ...state.recurringPayment,
          endDate: action.payload,
        },
      };
    }
    case "setEditingRecurringPayment": {
      return {
        ...state,
        recurringPayment: {
          ...state.recurringPayment,
          editing: action.payload,
        },
      };
    }
    case "updateRecurringPayment": {
      return {
        ...state,
        recurringPayment: {
          ...state.recurringPayment,
          ...action.payload,
        },
      };
    }
    case "setRecurringPaymentsOnlySuccess": {
      return { ...state, recurringPaymentsOnlySuccess: action.payload };
    }

    case "toggleApplyToExistingPledge": {
      let toggledValue = !state.applyToExistingPledge;
      return {
        ...state,
        applyToExistingPledge: toggledValue,
      };
    }

    case "updateMemo": {
      return {
        ...state,
        memo: action.payload,
      };
    }

    case "handlePaymentParent": {
      return {
        ...state,
        paymentParent: action.payload,
      };
    }

    case "setAddOnAmount": {
      let newAddOnAmounts = [...(state.addOnAmounts || [])];
      const foundIndex = newAddOnAmounts.findIndex(
        (a) => a.addOnID === action.payload.addOnID
      );
      if (foundIndex >= 0) newAddOnAmounts[foundIndex] = action.payload;
      else newAddOnAmounts.push(action.payload);
      return {
        ...state,
        addOnAmounts: newAddOnAmounts,
      };
    }

    case "removeAddOnAmount": {
      let newAddOnAmounts = [...state.addOnAmounts];
      const foundIndex = newAddOnAmounts.findIndex(
        (a) => a.addOnID === action.payload
      );
      if (foundIndex >= 0) newAddOnAmounts.splice(foundIndex, 1);
      return {
        ...state,
        addOnAmounts: newAddOnAmounts,
      };
    }

    case "updateAddOnGiftsAfterPaymentMethodSelect": {
      let newState = { ...state };
      const { addOnAmounts } = state;
      const ctfAmount = addOnAmounts.find((a) => a.type === "ctf");
      if (ctfAmount?.isChecked) {
        const ctfAddOnAmount = calculateAddOnGiftAmount(
          state.amount,
          addOnAmounts,
          {
            type: ctfAmount.type,
            presetAmount: null,
            percentage: null,
            addOnID: ctfAmount.addOnID,
          },
          ctfData,
          action.payload?.paymentType
        );
        const ctfAmountIndex = addOnAmounts.findIndex((a) => a.type === "ctf");
        addOnAmounts[ctfAmountIndex] = { ...ctfAmount, amount: ctfAddOnAmount };
        newState.addOnAmounts = addOnAmounts;
      }
      return newState;
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

function PaymentPageProvider({ children, initialState = {} }) {
  const initialStateWithDefaults = {
    ...defaultState,
    ...initialState,
  };
  const [state, dispatch] = useReducer(reducer, initialStateWithDefaults);

  state.isMobile = useIsMobile();

  return (
    <PaymentPageContext.Provider value={{ state, dispatch }}>
      {children}
    </PaymentPageContext.Provider>
  );
}

function usePaymentPage() {
  const context = React.useContext(PaymentPageContext);
  if (context === undefined) {
    throw new Error(`usePaymentPage must be used within a PaymentPageProvider`);
  }
  return context;
}

export {
  PaymentPageContext,
  PaymentPageProvider,
  usePaymentPage,
  defaultState,
};

function updateSelectedPaymentMethodID({
  paymentMethods,
  modifyPayment,
  currentlySelectedPaymentMethodID,
}) {
  // if they are modifying a scheduled payment, always select the card that was originally used
  if (modifyPayment?.paymentMethodID) return modifyPayment.paymentMethodID;

  // if there is a currentlySelected card, use it IF its in the paymentMethods array
  const isSelectedCardAvailable = paymentMethods.find(
    (method) => method.ID === currentlySelectedPaymentMethodID
  );

  if (currentlySelectedPaymentMethodID && isSelectedCardAvailable)
    return currentlySelectedPaymentMethodID;

  // if there are no payment methods, don't select anything!
  if (!paymentMethods || paymentMethods.length === 0) return null;

  const defaultCard = paymentMethods.find(
    (method) => method.isDefaultPaymentMethod
  );

  if (defaultCard?.isUnusable) {
    return null;
  }

  // if there is a default payment method, select it
  const defaultCardID = defaultCard?.ID;
  if (defaultCardID) return defaultCardID;

  // if no default card, return the first payment method
  const firstPaymentMethodID = paymentMethods[0].ID;
  if (firstPaymentMethodID) return firstPaymentMethodID;

  // failsafe: return null
  return null;
}
